<div class="crm-page invoices-page">
  <app-header
    [title]="'TITLES.INVOICES' | translate"
    [subtitle]="'COMPONENTS.INVOICES.SUBTITLE' | translate"
  >
    <div header-actions class="crm-page__header-actions">
      <div class="hidden lg:block">
        <app-navbar-profile-info></app-navbar-profile-info>
      </div>
    </div>
  </app-header>

  <div class="crm-page__content px-4 py-6 md:px-8 md:py-8">
    @if (isLoading) {
      <div class="crm-page__overlay">
        <div class="invoices-page__spinner" aria-hidden="true"></div>
        <span class="sr-only">{{ "MESSAGES.LOADING" | translate }}</span>
      </div>
    }

    <div class="crm-page__toolbar">
      <div class="crm-list-search form-search">
        <div class="input-search">
          <input
            type="text"
            [value]="searchTerm"
            [placeholder]="'COMPONENTS.INVOICES.SEARCH_PLACEHOLDER' | translate"
            [attr.aria-label]="'COMPONENTS.INVOICES.SEARCH_PLACEHOLDER' | translate"
            (input)="onSearchChange($any($event.target).value)"
          />
          <span class="search-icon" aria-hidden="true">
            <img src="assets/img/search.svg" alt="" />
          </span>
        </div>
      </div>

      <div class="invoices-page__filter-chips" role="group" [attr.aria-label]="'COMPONENTS.INVOICES.FIELDS.STATUS' | translate">
        @for (option of statusFilterOptions; track option.value) {
          <button
            type="button"
            class="invoices-page__filter-chip"
            [class.invoices-page__filter-chip--active]="statusFilter === option.value"
            (click)="onStatusFilterChange(option.value)"
          >
            {{ option.labelKey | translate }}
          </button>
        }
      </div>

      @if (canCreate$ | async) {
        <button
          type="button"
          class="crm-page__action-button crm-page__action-button--primary"
          (click)="openCreateModal()"
        >
          + {{ "COMPONENTS.INVOICES.BUTTONS.CREATE" | translate }}
        </button>
      }
    </div>

    @if (errorMessageKey) {
      <app-card-shell padding="lg">
        <div class="invoices-page__error">
          <div>
            <h2 class="invoices-page__error-title">{{ "ERRORS.UNKNOWN_ERROR" | translate }}</h2>
            <p class="invoices-page__error-text">{{ errorMessageKey | translate }}</p>
          </div>

          <button type="button" class="crm-page__action-button" (click)="retry()">
            {{ "BUTTONS.RETRY" | translate }}
          </button>
        </div>
      </app-card-shell>
    } @else if (invoices$ | async; as invoices) {
      @if (invoices.items.length === 0) {
        <app-card-shell class="crm-page__empty-card" padding="none">
          <div class="crm-page__empty-state">
            <app-empty-content
              [showImage]="true"
              [message]="'MESSAGES.EMPTY_LIST' | translate: { param: emptyListParam[currentLang] }"
              [tooltip]="'COMPONENTS.INVOICES.TABLE.EMPTY' | translate"
              [textPositionClass]="'text-center'"
            />
          </div>
        </app-card-shell>
      } @else {
        <app-card-shell class="crm-page__list-card" padding="none">
          <div class="crm-table__desktop">
            <div class="crm-table__scroller">
              <table class="crm-table__table">
                <thead>
                  <tr class="crm-table__header-row">
                    <th
                      class="crm-table__header-cell"
                      appSortableHeader="number"
                      [appSortableHeaderActiveColumn]="activeSortColumn"
                      [appSortableHeaderActiveOrder]="activeSortOrder"
                      (appSortableHeaderChange)="onSortChange($event)"
                    >
                      {{ "COMPONENTS.INVOICES.TABLE.NUMBER" | translate }}
                    </th>
                    <th class="crm-table__header-cell">
                      {{ "COMPONENTS.INVOICES.TABLE.CUSTOMER" | translate }}
                    </th>
                    <th class="crm-table__header-cell">
                      {{ "COMPONENTS.INVOICES.TABLE.ORDER" | translate }}
                    </th>
                    <th
                      class="crm-table__header-cell"
                      appSortableHeader="invoiceDate"
                      [appSortableHeaderActiveColumn]="activeSortColumn"
                      [appSortableHeaderActiveOrder]="activeSortOrder"
                      (appSortableHeaderChange)="onSortChange($event)"
                    >
                      {{ "COMPONENTS.INVOICES.TABLE.INVOICE_DATE" | translate }}
                    </th>
                    <th
                      class="crm-table__header-cell"
                      appSortableHeader="dueDate"
                      [appSortableHeaderActiveColumn]="activeSortColumn"
                      [appSortableHeaderActiveOrder]="activeSortOrder"
                      (appSortableHeaderChange)="onSortChange($event)"
                    >
                      {{ "COMPONENTS.INVOICES.TABLE.DUE_DATE" | translate }}
                    </th>
                    <th
                      class="crm-table__header-cell"
                      appSortableHeader="status"
                      [appSortableHeaderActiveColumn]="activeSortColumn"
                      [appSortableHeaderActiveOrder]="activeSortOrder"
                      (appSortableHeaderChange)="onSortChange($event)"
                    >
                      {{ "COMPONENTS.INVOICES.TABLE.STATUS" | translate }}
                    </th>
                    <th
                      class="crm-table__header-cell"
                      appSortableHeader="total"
                      [appSortableHeaderActiveColumn]="activeSortColumn"
                      [appSortableHeaderActiveOrder]="activeSortOrder"
                      (appSortableHeaderChange)="onSortChange($event)"
                    >
                      {{ "COMPONENTS.INVOICES.TABLE.TOTAL" | translate }}
                    </th>
                  </tr>
                </thead>

                <tbody>
                  @for (invoice of invoices.items; track invoice.id) {
                    <tr class="crm-table__row">
                      <td class="crm-table__cell crm-table__cell--title">
                        <button
                          type="button"
                          class="invoices-page__row-trigger"
                          [title]="invoice.number"
                          (click)="openDetailModal(invoice.id)"
                        >
                          {{ invoice.number }}
                        </button>
                      </td>
                      <td class="crm-table__cell" [title]="invoice.customerName">
                        {{ invoice.customerName }}
                      </td>
                      <td class="crm-table__cell" [title]="invoice.salesOrderNumber">
                        {{ invoice.salesOrderNumber }}
                      </td>
                      <td class="crm-table__cell" [title]="invoice.invoiceDate">
                        {{ invoice.invoiceDate ? (invoice.invoiceDate | date: "yyyy-MM-dd") : "-" }}
                      </td>
                      <td class="crm-table__cell" [title]="invoice.dueDate">
                        {{ invoice.dueDate ? (invoice.dueDate | date: "yyyy-MM-dd") : "-" }}
                      </td>
                      <td class="crm-table__cell">
                        <span class="invoices-page__status-badge" [ngClass]="invoice.statusBadgeClass">
                          {{ invoice.statusLabelKey | translate }}
                        </span>
                      </td>
                      <td class="crm-table__cell">{{ invoice.total | number: "1.2-2" }}</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          </div>

          <div class="crm-page__footer invoices-page__footer--desktop">
            <app-paginator
              class="crm-page__paginator"
              [totalItems]="invoices.totalCount"
              [page]="invoices.page"
              [pageSize]="invoices.pageSize"
              [showSummary]="true"
              (pageChange)="onPageChange($event)"
              (pageSizeChange)="onChangePageSize($event)"
            />
          </div>
        </app-card-shell>

        <div class="crm-table__mobile-section">
          <div class="crm-table__mobile-list">
            @for (invoice of invoices.items; track invoice.id) {
              <article class="crm-mobile-card">
                <div class="crm-mobile-card__header">
                  <div class="crm-mobile-card__title-wrap">
                    <span class="invoices-page__mobile-accent" aria-hidden="true"></span>

                    <div class="crm-mobile-card__title-block">
                      <button
                        type="button"
                        class="crm-mobile-card__title invoices-page__row-trigger"
                        [title]="invoice.number"
                        (click)="openDetailModal(invoice.id)"
                      >
                        {{ invoice.number }}
                      </button>
                      <span class="invoices-page__status-badge" [ngClass]="invoice.statusBadgeClass">
                        {{ invoice.statusLabelKey | translate }}
                      </span>
                    </div>
                  </div>
                </div>

                <div class="crm-mobile-card__grid">
                  <div class="crm-mobile-card__field">
                    <p class="crm-mobile-card__label">
                      {{ "COMPONENTS.INVOICES.TABLE.CUSTOMER" | translate }}
                    </p>
                    <p class="crm-mobile-card__value">{{ invoice.customerName }}</p>
                  </div>

                  <div class="crm-mobile-card__field">
                    <p class="crm-mobile-card__label">
                      {{ "COMPONENTS.INVOICES.TABLE.ORDER" | translate }}
                    </p>
                    <p class="crm-mobile-card__value">{{ invoice.salesOrderNumber }}</p>
                  </div>

                  <div class="crm-mobile-card__field">
                    <p class="crm-mobile-card__label">
                      {{ "COMPONENTS.INVOICES.TABLE.INVOICE_DATE" | translate }}
                    </p>
                    <p class="crm-mobile-card__value">
                      {{ invoice.invoiceDate ? (invoice.invoiceDate | date: "yyyy-MM-dd") : "-" }}
                    </p>
                  </div>

                  <div class="crm-mobile-card__field">
                    <p class="crm-mobile-card__label">
                      {{ "COMPONENTS.INVOICES.TABLE.DUE_DATE" | translate }}
                    </p>
                    <p class="crm-mobile-card__value">
                      {{ invoice.dueDate ? (invoice.dueDate | date: "yyyy-MM-dd") : "-" }}
                    </p>
                  </div>

                  <div class="crm-mobile-card__field">
                    <p class="crm-mobile-card__label">
                      {{ "COMPONENTS.INVOICES.TABLE.TOTAL" | translate }}
                    </p>
                    <p class="crm-mobile-card__value crm-mobile-card__value--count">
                      {{ invoice.total | number: "1.2-2" }}
                    </p>
                  </div>
                </div>
              </article>
            }
          </div>

          <div class="crm-page__footer invoices-page__footer--mobile">
            <app-paginator
              class="crm-page__paginator"
              [totalItems]="invoices.totalCount"
              [page]="invoices.page"
              [pageSize]="invoices.pageSize"
              [showSummary]="true"
              (pageChange)="onPageChange($event)"
              (pageSizeChange)="onChangePageSize($event)"
            />
          </div>
        </div>
      }
    }

    <app-reusable-modal
      [isOpen]="isCreateModalOpen"
      [panelClass]="'max-w-3xl'"
      [contentScoped]="true"
      [title]="'COMPONENTS.INVOICES.MODAL.CREATE_TITLE' | translate"
      [subtitle]="'COMPONENTS.INVOICES.MODAL.CREATE_SUBTITLE' | translate"
      (closed)="closeCreateModal()"
    >
      <app-create-invoice-modal
        [companyId]="companyId"
        [isOpen]="isCreateModalOpen"
        (cancelled)="closeCreateModal()"
        (created)="onInvoiceCreated($event)"
      />
    </app-reusable-modal>

    <app-reusable-modal
      [isOpen]="isDetailModalOpen"
      [panelClass]="'max-w-5xl'"
      [contentScoped]="true"
      [title]="'COMPONENTS.INVOICES.DETAIL.HEADER' | translate"
      (closed)="closeDetailModal()"
    >
      <app-invoice-modal
        [companyId]="companyId"
        [invoiceId]="selectedInvoiceId"
        [isOpen]="isDetailModalOpen"
        (closed)="closeDetailModal()"
        (paid)="onInvoicePaid()"
        (deleted)="onInvoiceDeleted()"
      />
    </app-reusable-modal>
  </div>
</div>
